import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, FileSearch, Sparkles } from "lucide-react";
import { practiceApi, openPracticeFile } from "../../lib/practiceApi";
import "./coordination.css";

const CONFIDENCE = { high: "alta", medium: "media", low: "bassa" };

export const CompanyVerify = ({ siteId, companyId, checklist, onSaved, testId }) => {
  const [documents, setDocuments] = useState([]);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef();

  const load = useCallback(() => practiceApi(`/coordination/${siteId}/companies/${companyId}/documents`)
    .then(setDocuments).catch(() => setDocuments([])), [siteId, companyId]);
  useEffect(() => { load(); }, [load]);

  async function verify(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setBusy(true); setError(""); setResults([]);
    const form = new FormData();
    files.forEach(file => form.append("files", file));
    try {
      const outcome = await practiceApi(`/coordination/${siteId}/companies/${companyId}/ai-verify`, { method: "POST", body: form });
      setResults(outcome.results || []);
      await load();
      onSaved();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  async function assign(fileId, docType) {
    if (!docType) return;
    setError("");
    try {
      await practiceApi(`/coordination/${siteId}/companies/${companyId}/documents/${fileId}/assign`, {
        method: "POST", body: JSON.stringify({ doc_type: docType }),
      });
      await load();
      onSaved();
    } catch (err) { setError(err.message); }
  }

  return <div className="company-verify" data-testid={testId}>
    <div className="company-verify-head">
      <div>
        <strong><FileSearch size={14} />Verifica documentale</strong>
        <small>Carica insieme tutti i documenti dell'impresa: INGEGO AI li riconosce e spunta gli adempimenti. Puoi sempre spuntarli a mano.</small>
      </div>
      <button className="ai-action" disabled={busy} data-testid={`${testId}-upload`} onClick={() => inputRef.current?.click()}>
        <Sparkles size={14} />{busy ? "Analizzo i documenti…" : "Verifica con INGEGO AI"}
      </button>
      <input type="file" multiple ref={inputRef} style={{ display: "none" }} onChange={verify} data-testid={`${testId}-input`} />
    </div>
    {error && <div className="error" role="alert" data-testid={`${testId}-error`}>{error}</div>}
    {!!results.length && <ul className="company-verify-results" data-testid={`${testId}-results`}>
      {results.map((row, index) => <li key={`${row.filename}-${index}`} className={row.verified ? "ok" : "todo"} data-testid={`${testId}-result-${index}`}>
        {row.verified ? <CheckCircle2 size={13} /> : <FileSearch size={13} />}
        <strong>{row.filename}</strong>
        <span>{row.message}{row.confidence ? ` · affidabilità ${CONFIDENCE[row.confidence] || row.confidence}` : ""}{row.expiry_date ? ` · scade il ${row.expiry_date.split("-").reverse().join("/")}` : ""}</span>
      </li>)}
    </ul>}
    {!!documents.length && <div className="company-verify-docs">
      <span className="company-verify-title">Documenti trasmessi ({documents.length})</span>
      {documents.map(doc => <div className="company-verify-doc" key={doc.id} data-testid={`${testId}-doc-${doc.id}`}>
        <button className="link" title="Apri documento" data-testid={`${testId}-open-${doc.id}`}
          onClick={() => openPracticeFile({ id: doc.id, original_filename: doc.filename, content_type: "application/pdf" })}>
          <Download size={12} />{doc.filename}
        </button>
        <span className={`practice-badge ${doc.matched ? "success" : "warning"}`}>{doc.matched ? doc.label : "DA ABBINARE"}</span>
        {doc.expiry_date && <small>scade il {doc.expiry_date.split("-").reverse().join("/")}</small>}
        {!doc.matched && <select defaultValue="" data-testid={`${testId}-assign-${doc.id}`} onChange={e => assign(doc.id, e.target.value)}>
          <option value="">Abbina a un adempimento…</option>
          {checklist.map(item => <option key={item.doc_type} value={item.doc_type}>{item.label}</option>)}
        </select>}
        {doc.ai_note && <em>{doc.ai_note}</em>}
      </div>)}
    </div>}
  </div>;
};
